import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { notification } from "antd";
import { useIntl } from "react-intl";

import { PermissionName } from "@mapmycustomers/shared";
import {
  Company,
  EntityType,
  EntityTypesSupportingCompanyAssociation,
  Person,
} from "@mapmycustomers/shared/types/entity";
import { PlatformFilterCondition } from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import { Button, Modal } from "@mapmycustomers/ui";

import noRecordsImageMap from "@app/component/associations/utils/noRecordsImages";
import CreateCompanyModal from "@app/component/createEditEntity/CreateCompanyModal";
import { hasPermission } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { MAX_COMPANIES_PER_PERSON } from "@app/util/consts";
import { useRecordLimitCheckWith } from "@app/util/hook/useRecordLimitCheck";

import Associations from "../components/Associations";
import {
  filterCompanies,
  getCompanies,
  getFilteredCompaniesCount,
  getTotalCompanies,
  isLoading,
} from "../store";
import useEntitySorter from "../utils/useEntitySorter";

import styles from "./CompanyAssociation.module.scss";
import messages from "./messages";

const LIMITS = {
  // High limits as this will never be possible
  [EntityType.ACTIVITY]: 1000,
  [EntityType.COMPANY]: 1000,
  [EntityType.DEAL]: 1000,
  [EntityType.PERSON]: MAX_COMPANIES_PER_PERSON,
  [EntityType.TRIP]: 1,
};

interface CompanyAssociationProps {
  allowAdd?: boolean;
  assignedCompanies?: Company[];
  associateWith?: Company["id"];
  canEditAssociatingRecord?: boolean;
  childSelect?: boolean;
  defaultQuery?: string;
  entityType: EntityTypesSupportingCompanyAssociation;
  fixedPerson?: Person;
  getListTitle?: (count: number, total: number) => string;
  hideOnSelect?: boolean;
  info?: React.ReactNode;
  multiselect?: boolean;
  okLoading?: boolean;
  okText?: string;
  onCompanyCreated?: (newCompany: Company) => void;
  onHide: (updated?: boolean) => void;
  onSelect?: (selectedCompaniesIds: Company["id"][], removedCompanyIds?: Company["id"][]) => void;
  onUpdatePrimaryCompany?: (accountId?: Company["id"]) => void;
  primaryCompanyId?: Company["id"];
  requestFilters?: PlatformFilterCondition[];
  suggestedCompanies?: Company[];
  supportsPrimaryEntity?: boolean;
  title?: ReactNode;
  zIndex?: number;
}

const CompanyAssociation = ({
  allowAdd = true,
  assignedCompanies,
  associateWith,
  canCreateCompanies,
  canEditAssociatingRecord,
  childSelect = false,
  companies,
  defaultQuery = "",
  entityType,
  filtered,
  fixedPerson,
  getListTitle,
  hideOnSelect = true,
  info,
  loading,
  multiselect = true,
  okLoading,
  okText,
  onCompanyCreated,
  onHide,
  onSearch,
  onSelect,
  onUpdatePrimaryCompany,
  primaryCompanyId,
  requestFilters,
  suggestedCompanies,
  supportsPrimaryEntity,
  title,
  total,
  zIndex,
}: CompanyAssociationProps & typeof mapDispatchToProps & ReturnType<typeof mapStateToProps>) => {
  const intl = useIntl();

  const [query, setQuery] = useState<string>(defaultQuery);
  const [newSelectedCompanyIds, setNewSelectedCompanyIds] = useState<Set<Company["id"]>>(
    () => new Set()
  );
  const [removedCompanyIds, setRemovedCompanyIds] = useState<Set<Company["id"]>>(() => new Set());

  const [internalPrimaryCompanyId] = useState<Company["id"] | undefined>(primaryCompanyId);

  // Child company object has parentAccount property which is used to filter all the already associated parent companies based on associateWith
  const assignedList = useMemo(() => {
    return assignedCompanies
      ? assignedCompanies
      : companies && associateWith && multiselect && childSelect
        ? companies.filter(({ parentAccount }) => parentAccount?.id === associateWith)
        : [];
  }, [assignedCompanies, associateWith, childSelect, companies, multiselect]);

  const [createdCompanies, setCreatedCompanies] = useState<Company[]>([]);
  const sortedCompanies = useEntitySorter(
    companies ?? [],
    createdCompanies,
    query.trim().length > 0,
    assignedList,
    suggestedCompanies,
    primaryCompanyId
  );

  const assignedCompanyIds = useMemo(
    () => new Set(assignedList?.map(({ id }) => id)),
    [assignedList]
  );

  const isCompanySelected = useCallback(
    (id: Company["id"]) => {
      const wasInitiallySelectedAndNotRemoved =
        assignedCompanyIds.has(id) && !removedCompanyIds.has(id);

      const wasNewlySelected = newSelectedCompanyIds.has(id);

      return wasInitiallySelectedAndNotRemoved || wasNewlySelected;
    },
    [assignedCompanyIds, newSelectedCompanyIds, removedCompanyIds]
  );

  const visibleCompanyIds = useMemo(() => sortedCompanies.map(({ id }) => id), [sortedCompanies]);

  const selectedCompanyIds = useMemo(
    () => visibleCompanyIds.filter((id) => isCompanySelected(id)),
    [isCompanySelected, visibleCompanyIds]
  );

  useEffect(() => {
    onSearch({
      companyId: associateWith,
      isChildSelection: childSelect,
      query: defaultQuery,
      requestFilters,
    });
  }, [associateWith, childSelect, defaultQuery, onSearch, requestFilters]);

  const handleSearch = useCallback(
    (query: string) => {
      onSearch({ companyId: associateWith, isChildSelection: childSelect, query, requestFilters });
    },
    [associateWith, childSelect, onSearch, requestFilters]
  );

  const [createNewCompanyVisible, showCreateCompanyModal, hideCreateCompanyModal] = useBoolean();
  const handleHideCreateCompanyModal = useDynamicCallback((newCompany?: Company) => {
    const alreadyAssignedCompaniesIds = new Set(
      [...(assignedList ?? []), ...createdCompanies].map(({ id }) => id)
    );
    const hadAnySelectedCompaniesAlready = selectedCompanyIds.some(
      (id) => !alreadyAssignedCompaniesIds.has(id)
    );
    if (newCompany) {
      if (!childSelect && !onCompanyCreated) {
        setNewSelectedCompanyIds(
          (ids) => new Set(multiselect ? [...ids, newCompany.id] : [newCompany.id])
        );
      }
      // we also store created companies, just to show then in the top of the list, to not confuse user
      // because otherwise, newly added record could get filtered out according to current filter
      // that's also the reason why we reset the filter here
      setCreatedCompanies((companies) => [...companies, newCompany]);
      setQuery("");
      onSearch({
        companyId: associateWith,
        isChildSelection: childSelect,
        query: "",
        requestFilters,
      });
      onCompanyCreated?.(newCompany);
    }
    hideCreateCompanyModal();

    // if user selected no companies in the modal before adding new company, then just hide both modals
    if (!hadAnySelectedCompaniesAlready && newCompany) {
      onSelect?.([newCompany.id], Array.from(removedCompanyIds));
      if (hideOnSelect) {
        onHide(true);
      }
    }
  });

  const hasChanges = multiselect
    ? newSelectedCompanyIds.size > 0 || removedCompanyIds.size > 0
    : newSelectedCompanyIds.size > 0;

  const alreadyAssignedEntities = useMemo(
    () => [...assignedList, ...createdCompanies],
    [assignedList, createdCompanies]
  );

  const handleRowsChange = useCallback(
    (selectedRows: Company["id"][]) => {
      const currentlySelected = new Set(selectedRows);

      const updatedNewSelectedCompanyIds = new Set(newSelectedCompanyIds);
      const updatedRemovedCompanyIds = new Set(removedCompanyIds);

      for (const id of visibleCompanyIds) {
        const wasInitiallyAssigned = assignedCompanyIds.has(id);
        const isNowSelected = currentlySelected.has(id);

        if (isNowSelected) {
          if (wasInitiallyAssigned) {
            updatedRemovedCompanyIds.delete(id);
          } else {
            updatedNewSelectedCompanyIds.add(id);
          }
        } else {
          if (wasInitiallyAssigned) {
            updatedRemovedCompanyIds.add(id);
          } else {
            updatedNewSelectedCompanyIds.delete(id);
          }
        }
      }

      setNewSelectedCompanyIds(updatedNewSelectedCompanyIds);
      setRemovedCompanyIds(updatedRemovedCompanyIds);
    },
    [assignedCompanyIds, visibleCompanyIds, newSelectedCompanyIds, removedCompanyIds]
  );

  const modalSubTitle = useMemo(() => {
    const subTitle = [];

    if (newSelectedCompanyIds.size > 0) {
      subTitle.push(
        intl.formatMessage(messages.selectedCount, {
          count: newSelectedCompanyIds.size,
        })
      );
    }
    if (removedCompanyIds.size > 0) {
      subTitle.push(
        intl.formatMessage(messages.deselectCount, {
          count: removedCompanyIds.size,
        })
      );
    }

    return subTitle.join(" • ");
  }, [intl, newSelectedCompanyIds.size, removedCompanyIds.size]);

  const handleOkClick = useCallback(() => {
    const limit = LIMITS[entityType];
    if (limit && selectedCompanyIds.length > limit) {
      notification.error({
        description: intl.formatMessage(messages.associationLimitError, {
          entity: intl.formatMessage(messages[entityType]),
          limit,
        }),
        message: intl.formatMessage(messages.errorTitle),
      });
      return;
    }

    // Block linking a company the user can't edit instead of failing silently after save.
    // Child selection edits the picked companies; otherwise the record being associated is edited.
    const hasNonEditableChange = childSelect
      ? Array.from(newSelectedCompanyIds).some(
          (id) =>
            sortedCompanies.find((company) => company.id === id)?.accessStatus?.update === false
        )
      : canEditAssociatingRecord === false;
    if (hasNonEditableChange) {
      notification.error({
        description: intl.formatMessage(messages.notEditable),
        message: intl.formatMessage(messages.notEditableTitle),
      });
      return;
    }

    onSelect?.(Array.from(newSelectedCompanyIds), Array.from(removedCompanyIds));
    if (hideOnSelect) {
      onHide(true);
    }
  }, [
    canEditAssociatingRecord,
    childSelect,
    entityType,
    hideOnSelect,
    intl,
    newSelectedCompanyIds,
    onHide,
    onSelect,
    removedCompanyIds,
    selectedCompanyIds.length,
    sortedCompanies,
  ]);

  const handleCancel = useCallback(() => {
    if (internalPrimaryCompanyId !== primaryCompanyId) {
      onUpdatePrimaryCompany?.(internalPrimaryCompanyId);
    }
    onHide(false);
  }, [internalPrimaryCompanyId, onHide, onUpdatePrimaryCompany, primaryCompanyId]);

  const handleAddClick = useRecordLimitCheckWith(showCreateCompanyModal);

  return (
    <Modal
      className={styles.modal}
      okButtonProps={{
        disabled: (!hasChanges && internalPrimaryCompanyId === primaryCompanyId) || okLoading,
        loading: okLoading,
      }}
      okText={
        okText ??
        intl.formatMessage(messages.footerOkButton, {
          addCompanies: assignedList?.length === 0 || !assignedList,
          multiselect,
        })
      }
      onCancel={handleCancel}
      onOk={handleOkClick}
      open
      title={
        <div className={styles.modalTitle}>
          <span>{title ?? intl.formatMessage(messages.header, { multiselect })}</span>
          {modalSubTitle.length > 0 && <span className={styles.titleSubText}>{modalSubTitle}</span>}
        </div>
      }
      width="clamp(500px, 50vw, 750px)"
      zIndex={zIndex}
    >
      {info}
      <Associations<Company>
        alreadyAssociatedMessage={intl.formatMessage(messages.alreadyAssociated)}
        assignedRecords={alreadyAssignedEntities}
        associateWith={associateWith}
        cantAssociateWithSelfMessage={intl.formatMessage(messages.cantAssociateWithSelf)}
        disableNonEditableEntities={childSelect}
        entities={sortedCompanies}
        extras={
          allowAdd && canCreateCompanies ? (
            <Button onClick={handleAddClick} type="link">
              {intl.formatMessage(messages.addNewCompanyButton)}
            </Button>
          ) : undefined
        }
        filtered={filtered}
        getListTitle={getListTitle}
        loading={loading}
        multiselect={multiselect}
        noDataImageSrc={noRecordsImageMap[EntityType.COMPANY]}
        notEditableMessage={intl.formatMessage(messages.notEditableRowTooltip)}
        onChange={handleRowsChange}
        onChangeQuery={setQuery}
        onSearch={handleSearch}
        onUpdatePrimaryEntity={onUpdatePrimaryCompany}
        placeholder={intl.formatMessage(messages.placeholder)}
        primaryEntityId={primaryCompanyId}
        query={query}
        suggestedEntities={suggestedCompanies}
        supportsPrimaryEntity={supportsPrimaryEntity}
        total={total}
        value={selectedCompanyIds}
      />
      {createNewCompanyVisible && (
        <CreateCompanyModal
          fixedParentCompanyId={childSelect ? associateWith : undefined}
          fixedPerson={fixedPerson}
          okText={intl.formatMessage(messages.modalOkButton)}
          onHide={handleHideCreateCompanyModal}
        />
      )}
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  canCreateCompanies: hasPermission(state)(PermissionName.COMPANY_CREATE),
  companies: getCompanies(state),
  filtered: getFilteredCompaniesCount(state),
  loading: isLoading(state),
  total: getTotalCompanies(state),
});

const mapDispatchToProps = {
  onSearch: filterCompanies.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(CompanyAssociation);
